import * as React from "react";
import { Footer } from "@/ui/components/Footer";
import { MobileHeader } from "@/ui/components/MobileHeader";
import { useAppSelector } from "@/store/hooks";
import { selectIsAuthenticated } from "@/store/slices/authSlice";

export interface AuthLayoutProps {
  /**
   * Content to render inside the layout
   */
  children: React.ReactNode;
  /**
   * Additional className for the root element
   */
  className?: string;
}

/**
 * AuthLayout - Layout for unauthenticated pages (login, signup, etc.)
 *
 * Features:
 * - MobileHeader with menu (same as AppLayout)
 * - Background image (all screens, 100% opacity)
 * - White centered container with shadow (max-w: 21.4375rem / 343px)
 * - Footer (all screens, relative positioning)
 *
 * Container Styling (Figma):
 * - Background: #FFF (white)
 * - Border radius: 5px
 * - Box shadow: 1px 1px 3px 0 rgba(0, 0, 0, 0.25)
 * - Padding: 24px (p-6)
 *
 * Usage:
 * - LoginPage, SignupPage, ForgotPasswordPage, ResetPasswordPage, VerifyPage, CallbackPage
 *
 * @example
 * ```tsx
 * export default function LoginPage() {
 *   return (
 *     <AuthLayout>
 *       <LoginForm />
 *     </AuthLayout>
 *   );
 * }
 * ```
 */
export const AuthLayout = React.forwardRef<HTMLDivElement, AuthLayoutProps>(
  ({ children }, ref) => {
    const isAuthenticated = useAppSelector(selectIsAuthenticated);

    return (
      <div ref={ref} className="relative min-h-screen flex flex-col">
        {/* Background - all screens, full opacity */}
        <div
          className="absolute inset-0 bg-[url('/images/app-bg.jpg')] bg-cover bg-center pointer-events-none -z-10"
          aria-hidden="true"
        />

        {/* MobileHeader - same as AppLayout */}
        <MobileHeader fixed={true} isAuthenticated={isAuthenticated} />

        {/* Content area - flex-1 to push footer down */}
        <div className="flex-1 flex items-center justify-center px-4 py-8">
          {/* Centered content container with white background and shadow */}
          <div className="w-full max-w-[21.4375rem] relative z-10 bg-white p-6 rounded-[0.3125rem] shadow-[1px_1px_3px_0_rgba(0,0,0,0.25)]">
            {children}
          </div>
        </div>

        {/* Footer - relative positioning */}
        <Footer className="relative z-10" />
      </div>
    );
  }
);

AuthLayout.displayName = "AuthLayout";
